import React, { useEffect, useLayoutEffect, useState, useCallback, useRef, useMemo } from 'react';
import { View, Text, StyleSheet, RefreshControl, AccessibilityInfo, Alert, Modal, Platform } from 'react-native';
import { FlatList } from 'react-native-gesture-handler';
import Animated, { createAnimatedComponent, useSharedValue, useAnimatedScrollHandler, useAnimatedStyle, withTiming } from 'react-native-reanimated';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useRoute, RouteProp, useFocusEffect } from '@react-navigation/native';
import { useAuthNavigation } from '../shared/navigation/useAuthNavigation';
import { useQueryClient } from '@tanstack/react-query';
import moment from 'moment';
import { appTheme, useThemeColors } from '../shared/theme';
import { resolveMenuFlowStudioHeaderHeights } from '../foodMenu/components/menuFlowHeaderLayout';
import { MainLoader } from '../components/load-main/MainLoader';
import type { ThemeColors } from '../shared/theme/store';
import { useAuthStore } from '../shared/auth/store';
import { useSubscriptionContext, getDeliveryDatesConsumedByDoubleBox, canEnableNewDoubleBoxForDate, collectDoubleBoxAnchorDates, patchSubscriptionDoubleBoxDateInCache, scheduleDeferredSubscriptionRefetch } from '../data/domains/subscription';
import {
  useMenuForDateQuery,
  MENU_CACHE_STALE_MS,
  MENU_CACHE_GC_MS,
  isMenuDateSlotDivergentFromServer,
  useMenuStore,
  pickBuildersForMenuMerge,
  useMenuState,
  useMenuSync,
  useMenuBooking,
  fetchMenu,
  isMenuInvalidationAllowedForDate,
  hasPastDateMenuCached,
  menuCacheTimingForDate,
  menuKeys,
  prepareMenuBuildersWithHealthPolicy,
  filterMenuBuildersHidingMandatoryAllergyConflicts,
  buildMenuFlowTabbedListCache,
  filterMenuBuildersForLoggedOutRamadanMenu,
  useGuestDisplayMenuDates,
  countMealsAndSnacksFromMenuBuilders,
  findMenuItemByBuilderAndId,
  guestDisplayMenuTodayIso,
  sortGuestMainMenuBuilders,
  type MenuBuilder,
  type MenuItemData,
  type MenuFlowFlatRow,
} from '../data/domains/foodMenu';
import { useMenuCalendarRefreshOnLeave } from '../data/domains/foodMenu/useMenuCalendarRefreshOnLeave';
import { ensureAsciiDateString } from '../shared/utils/apiParams';
import {
  useProfileAllergiesDislikesSync,
  useSelectedAllergyIds,
  useSelectedDislikeIds,
} from '../data/domains/preferences';
import { DailyCustomizationModal } from '../foodMenu/components/DailyCustomizationModal';
import type { CustomizationGroup } from '../foodMenu/components/DailyCustomizationModal';
import { MenuFlowItemCard, MENU_FLOW_ITEM_ROW_HEIGHT } from '../foodMenu/components/MenuFlowItemCard';
import { MENU_FLOW_FLAT_LIST_PROPS } from '../shared/utils/scrollListPerf';
import { Toast, type ToastVariant } from '../components/common/Toast';
import { IssueReportState } from '../components/common/IssueReportState';
import { Clickable } from '../components/common/Clickable';
import { MenuFlowHeader } from '../foodMenu/components/MenuFlowHeader';
import { MenuFlowBottomMacroBar, getMenuListBottomContentPadding, type MenuSaveBarStatus } from '../foodMenu/components/MenuFlowBottomMacroBar';
import { useTranslation, getApiText } from '../shared/i18n';
import { useAuthSheets } from '../shared/auth/authSheetContext';
import { promptAccountRequiredAlert } from '../shared/auth/promptAccountRequiredAlert';
import { DASH_STUDIO_RADIUS, studioCardShell, STACK_SCROLL_GUTTER, ScreenShell } from '../shared/theme/studio/stackScreenShell';
import { GuestBrowseBackFooter, guestBrowseBackFooterScrollPad } from '../foodMenu/components/GuestBrowseBackFooter';
import { resolveMenuPreviewModeWithProbe, shouldDisableMenuSelectionForProbe } from '../foodMenu/menuPreviewMode';
import { useSaveMenuFlowEnabled, useNutritionUiEnabled, useAllergyPickerMode, useLoggedOutRamadanMenuEnabled } from '../data/domains/config';

type MenuFlowRouteParams = {
  MenuFlow: { date?: string; fromCalendar?: boolean; guestBrowse?: boolean };
  /** Same params when registered under the main tab as `DailyMenuFlow`. */
  DailyMenuFlow: { date?: string; fromCalendar?: boolean; guestBrowse?: boolean };
};

const SCROLL_RANGE = 260;
const BOTTOM_BAR_PADDING = 140;
/** Space used for the subtle section divider row in the flat list (scroll/layout must match). */
const SECTION_GAP = 3;
const POST_SAVE_BAR_MS = 5000;
const SAVED_TO_SYNCED_CHIP_MS = 2600;

const AnimatedFlatList = createAnimatedComponent(FlatList<MenuFlowFlatRow>);

function createFoodDailyMenuFlowScreenStyles(c: ThemeColors) {
  return StyleSheet.create({
    container: { flex: 1, backgroundColor: 'transparent' },
    contentLayer: { flex: 1, zIndex: 1 },
    overlayLayer: {
      ...StyleSheet.absoluteFillObject,
      zIndex: 20,
      elevation: 20,
      pointerEvents: 'box-none',
    },
    toastLayer: {
      ...StyleSheet.absoluteFillObject,
      zIndex: 12_000_000,
      elevation: 12_000_000,
      pointerEvents: 'box-none',
    },
    sectionsWrap: { flex: 1, zIndex: 9 },
    /** Shared by animated wrapper + FlatList so both expand. */
    sectionsFlex: { flex: 1 },
    sectionsContent: {
      paddingHorizontal: 0,
      paddingBottom: BOTTOM_BAR_PADDING,
    },
    loadingContainer: { flex: 1, justifyContent: 'center', alignItems: 'center' },
    loadingText: { marginTop: 10, color: c.textSecondary },
    emptyContainer: { flex: 1, justifyContent: 'center', alignItems: 'center', padding: 40 },
    emptyText: { textAlign: 'center', color: c.textSecondary },
    flatItemWrap: { marginHorizontal: STACK_SCROLL_GUTTER },
    replaceableModalOverlay: { flex: 1, backgroundColor: c.backdrop, justifyContent: 'center', padding: 24 },
    replaceableModalContent: {
      ...studioCardShell(c),
      borderRadius: DASH_STUDIO_RADIUS,
      borderWidth: 0,
      padding: 16,
      maxHeight: 360,
    },
    replaceableModalTitle: { fontSize: 17, fontWeight: '600', marginBottom: 12, color: c.text },
    replaceableModalRow: {
      borderBottomWidth: StyleSheet.hairlineWidth,
      borderBottomColor: c.borderLight,
      paddingVertical: appTheme.spacing.md,
      marginBottom: appTheme.spacing.sm,
    },
    replaceableModalRowText: { fontSize: 16, color: c.text },
    replaceableModalCancel: { marginTop: 12, paddingVertical: 12, alignItems: 'center' },
    replaceableModalCancelText: { fontSize: 16, color: c.textSecondary },
    subscriptionModalMessage: { fontSize: 16, color: c.text, textAlign: 'center', marginBottom: 20, lineHeight: 22 },
    subscriptionModalOk: {
      backgroundColor: c.primary,
      paddingVertical: 14,
      borderRadius: 12,
      alignItems: 'center',
      justifyContent: 'center',
      alignSelf: 'stretch',
      width: '100%',
    },
    subscriptionModalOkText: {
      color: c.onPrimary,
      fontSize: 16,
      fontWeight: '600',
      textAlign: 'center',
      width: '100%',
    },
    sectionDivider: {
      height: SECTION_GAP,
      backgroundColor: c.divider,
      marginHorizontal: 28,
      alignSelf: 'center',
      maxWidth: '72%',
      borderRadius: 2,
    },
  });
}

const FoodDailyMenuFlowScreen = () => {
  const insets = useSafeAreaInsets();
  const { t, locale } = useTranslation();
  const { openLogin, openRegister } = useAuthSheets();
  const themeColors = useThemeColors();
  const styles = useMemo(() => createFoodDailyMenuFlowScreenStyles(themeColors ?? appTheme.colors), [themeColors]);
  const menuItemThemeKey = useMemo(
    () => [themeColors?.primary, themeColors?.surface, themeColors?.text, themeColors?.error, themeColors?.calorie, themeColors?.protein, themeColors?.carbs, themeColors?.fats].join('|'),
    [themeColors?.primary, themeColors?.surface, themeColors?.text, themeColors?.error, themeColors?.calorie, themeColors?.protein, themeColors?.carbs, themeColors?.fats],
  );
  const navigation = useAuthNavigation();
  const route = useRoute<RouteProp<MenuFlowRouteParams, 'MenuFlow' | 'DailyMenuFlow'>>();
  const queryClient = useQueryClient();
  const user = useAuthStore(s => s.user);
  const customerId = user?.id;
  useMenuCalendarRefreshOnLeave(customerId);
  const initialDateFromCalendar = route.params?.date;
  const from_calendar_nav = route.params?.fromCalendar === true;
  const guest_browse_from_main = route.params?.guestBrowse === true;
  const save_menu_flow = useSaveMenuFlowEnabled();
  const nutrition_ui_enabled = useNutritionUiEnabled();
  /** type4 = food-menu set1 clone — no menu_flow_variant switching. */
  const menu_scroll_range = SCROLL_RANGE;
  const flat_item_wrap_style = styles.flatItemWrap;
  const MenuFlowItemCardResolved = MenuFlowItemCard;
  const MenuFlowHeaderResolved = MenuFlowHeader;
  const MenuFlowBottomMacroBarResolved = MenuFlowBottomMacroBar;
  const getMenuListBottomContentPaddingResolved = getMenuListBottomContentPadding;
  const go_main = useCallback(() => {
    if (navigation.canGoBack()) {
      navigation.goBack();
      return;
    }
    (navigation as { navigate: (name: string) => void }).navigate('Main');
  }, [navigation]);
  const subscription_context = useSubscriptionContext();
  const { refetch: refetchSubscription, activeSubscriptions } = subscription_context;
  const [pendingDoubleBoxDates, setPendingDoubleBoxDates] = useState<Set<string>>(new Set());

  const { setOnSyncError, isRequestSyncing, isSyncing: is_menu_syncing, flushSyncForDate } = useMenuSync({
    manualOnly: save_menu_flow,
    onSyncSuccess: useCallback(
      (date: string, isDoubleBox: boolean) => {
        if (isDoubleBox) {
          setPendingDoubleBoxDates(prev => {
            const next = new Set(prev);
            next.delete(date);
            return next;
          });
          refetchSubscription();
        }
      },
      [refetchSubscription, customerId, queryClient],
    ),
  });

  useProfileAllergiesDislikesSync(user?.id);
  const selected_allergy_ids = useSelectedAllergyIds();
  const selected_dislike_ids = useSelectedDislikeIds();
  const allergy_picker_mode = useAllergyPickerMode();
  const logged_out_ramadan_menu = useLoggedOutRamadanMenuEnabled();

  const { getDayConfig } = subscription_context;
  const isLoadingSubscriptions = subscription_context.isLoading;
  const subscriptionData = useMemo(
    () => ({
      subscriptions: subscription_context.subscriptions,
      availableDates: subscription_context.availableDates,
      dayConfigMap: subscription_context.dayConfigMap,
    }),
    [subscription_context.subscriptions, subscription_context.availableDates, subscription_context.dayConfigMap],
  );

  const { dates: preview_week_dates, ensureGuestDateInRange, resolveGuestDatesIncluding } =
    useGuestDisplayMenuDates();

  const menu_preview = useMemo(
    () =>
      resolveMenuPreviewModeWithProbe({
        guest_browse_from_route: guest_browse_from_main,
        customer_id: customerId,
        is_loading_subscriptions: isLoadingSubscriptions,
        subscriptions: subscription_context.subscriptions,
        active_subscription_count: activeSubscriptions.length,
      }),
    [guest_browse_from_main, customerId, isLoadingSubscriptions, activeSubscriptions.length, subscription_context.subscriptions],
  );
  const {
    is_guest_from_main,
    preview_week_dates_mode,
    show_guest_back_footer,
    guest_menu_preview,
    should_wait_for_subscriptions,
  } = menu_preview;

  const double_box_anchor_set = useMemo(
    () => new Set(collectDoubleBoxAnchorDates(subscriptionData.subscriptions ?? [], pendingDoubleBoxDates)),
    [subscriptionData.subscriptions, pendingDoubleBoxDates],
  );

  /** Calendar D+1 after any double-box anchor — always locked for selection. */
  const forcedDisabledDates = useMemo(() => {
    if (preview_week_dates_mode) {
      return [];
    }
    const avail = (subscriptionData.availableDates ?? []).map(d => ensureAsciiDateString(d) || d);
    return getDeliveryDatesConsumedByDoubleBox(
      avail,
      date => double_box_anchor_set.has(date) || Boolean(getDayConfig(date)?.isDoubleBox),
      double_box_anchor_set,
    );
  }, [preview_week_dates_mode, subscriptionData?.availableDates, getDayConfig, double_box_anchor_set]);

  const forcedDisabledDateSet = useMemo(() => new Set(forcedDisabledDates), [forcedDisabledDates]);

  /** Refresh React Query menu GET for a date without blocking navigation (previous date after switch, or on leave). */
  const prefetchMenuDateInBackground = useCallback(
    (dateRaw: string) => {
      if (typeof customerId !== 'number') {
        return;
      }
      const date_ascii = ensureAsciiDateString(dateRaw) || dateRaw;
      if (date_ascii.length < 10) {
        return;
      }
      if (hasPastDateMenuCached(queryClient, customerId, date_ascii, false)) {
        return;
      }
      const { staleTime, gcTime } = menuCacheTimingForDate(date_ascii);
      const run = () => {
        queryClient.prefetchQuery({
          queryKey: menuKeys.day(customerId, date_ascii, false),
          queryFn: () => fetchMenu(customerId, date_ascii, false),
          staleTime,
          gcTime,
        });
        if (getDayConfig(date_ascii)?.isDoubleBox) {
          if (hasPastDateMenuCached(queryClient, customerId, date_ascii, true)) {
            return;
          }
          queryClient.prefetchQuery({
            queryKey: menuKeys.day(customerId, date_ascii, true),
            queryFn: () => fetchMenu(customerId, date_ascii, true),
            staleTime,
            gcTime,
          });
        }
      };
      setTimeout(run, 0);
    },
    [customerId, queryClient, getDayConfig, pendingDoubleBoxDates],
  );

  const {
    setMenuData,
    setSelectedDate,
    getSlot,
    setItemCustomizations,
    updateItemQuantity,
    updateItemScaledCarbs,
    selectedDate,
    dayConfig: storeDayConfig,
    menuBuilders,
    selectedMealsCount,
    selectedSnacksCount,
    mealsCount,
    snacksCount,
    macroTotals,
    isSelectionComplete,
  } = useMenuState();

  useLayoutEffect(() => {
    if (!is_guest_from_main) {
      return;
    }
    const ascii = ensureAsciiDateString(selectedDate) || selectedDate;
    if (ascii.length >= 10 && preview_week_dates.includes(ascii)) {
      return;
    }
    const today = guestDisplayMenuTodayIso();
    setSelectedDate(today);
    setMenuData([], today, null, false);
  }, [is_guest_from_main, selectedDate, preview_week_dates, setSelectedDate, setMenuData]);

  /** Guest main browse only — meals A→Z then snacks A→Z; subscribed menu keeps API order. */
  const display_menu_builders = useMemo(() => {
    const health_filtered =
      allergy_picker_mode === 'combined'
        ? filterMenuBuildersHidingMandatoryAllergyConflicts(menuBuilders, selected_allergy_ids)
        : menuBuilders;
    const filtered = filterMenuBuildersForLoggedOutRamadanMenu(health_filtered, {
      is_guest_from_main,
      logged_out_ramadan_menu,
    });
    if (!guest_menu_preview || !filtered?.length) {
      return filtered;
    }
    return sortGuestMainMenuBuilders(filtered, locale);
  }, [
    guest_menu_preview,
    menuBuilders,
    locale,
    is_guest_from_main,
    logged_out_ramadan_menu,
    allergy_picker_mode,
    selected_allergy_ids,
  ]);

  /** Start guest preview fetch before the date-init effect writes `selectedDate` into the store. */
  const menu_query_date = useMemo(() => {
    const ascii = ensureAsciiDateString(selectedDate) || selectedDate;
    if (ascii.length >= 10) {
      return ascii;
    }
    if (is_guest_from_main || preview_week_dates_mode) {
      return guestDisplayMenuTodayIso();
    }
    return ascii;
  }, [selectedDate, is_guest_from_main, preview_week_dates_mode]);

  const is_date_dirty = useMenuStore(s => {
    const d = ensureAsciiDateString(s.selectedDate) || s.selectedDate;
    return isMenuDateSlotDivergentFromServer(s.menuStateByDate[d], s.serverSnapshotByDate[d]);
  });
  const [v3SaveUi, setV3SaveUi] = useState<MenuSaveBarStatus>('synced');
  const [v3BarSuppressed, setV3BarSuppressed] = useState(false);
  const [v3PostSave, setV3PostSave] = useState(false);
  const v3_to_synced_ref = useRef<ReturnType<typeof setTimeout> | null>(null);
  const v3_hide_bar_ref = useRef<ReturnType<typeof setTimeout> | null>(null);
  const v3_prev_date_ref = useRef<string | null>(null);

  const [selectedFoodType, setSelectedFoodType] = useState<string>('All');
  const selectedFoodTypeRef = useRef(selectedFoodType);
  selectedFoodTypeRef.current = selectedFoodType;
  const [toastMessage, setToastMessage] = useState('');
  const [toastVariant, setToastVariant] = useState<ToastVariant>('default');
  const [showToast, setShowToast] = useState(false);
  const [isPullRefreshing, setIsPullRefreshing] = useState(false);
  const [reduceMotion, setReduceMotion] = useState(false);
  const [customizationModalVisible, setCustomizationModalVisible] = useState(false);
  const [customizationItem, setCustomizationItem] = useState<MenuItemData | null>(null);
  const pendingPlusItemIdRef = useRef<number | null>(null);
  const pendingPlusMenuBuilderIdRef = useRef<number | null>(null);
  const [activeBox, setActiveBox] = useState<'box1' | 'box2'>('box1');

  const scrollY = useSharedValue(0);
  const sectionOffsetsSV = useSharedValue<number[]>([]);
  const currentSectionIndexSV = useSharedValue(0);
  const collapsedHeaderBottomSV = useSharedValue(0);
  const lastDateSliderCollapsed = useSharedValue(false);
  const listOpacitySV = useSharedValue(1);
  const prevActiveBoxRef = useRef(activeBox);
  /** Native scroll handle for `requireExternalGestureToFail` on menu item cards (avoids tap/pan firing while scrolling). */
  const menuFlatListGestureRef = useRef<React.ElementRef<typeof FlatList<MenuFlowFlatRow>> | null>(null);

  const handleSelectFoodType = useCallback(
    (type: string) => {
      if (type === selectedFoodTypeRef.current) {
        return;
      }
      setSelectedFoodType(type);
      currentSectionIndexSV.value = 0;
      scrollY.value = 0;
      menuFlatListGestureRef.current?.scrollToOffset?.({ offset: 0, animated: false });
    },
    [currentSectionIndexSV, scrollY],
  );

  useEffect(() => {
    if (prevActiveBoxRef.current !== activeBox) {
      prevActiveBoxRef.current = activeBox;
      if (reduceMotion) {
        listOpacitySV.value = 1;
        return;
      }
      listOpacitySV.value = 0.92;
      listOpacitySV.value = withTiming(1, { duration: 140 });
    }
  }, [activeBox, listOpacitySV, reduceMotion]);

  const listOpacityStyle = useAnimatedStyle(() => ({ opacity: listOpacitySV.value }));

  useEffect(() => {
    const sub = AccessibilityInfo.addEventListener('reduceMotionChanged', setReduceMotion);
    AccessibilityInfo.isReduceMotionEnabled().then(setReduceMotion);
    return () => sub.remove();
  }, []);

  const leaveRef = useRef({ customerId: undefined as number | undefined, selectedDate: '' });
  leaveRef.current = { customerId: customerId, selectedDate: selectedDate };

  /**
   * Studio set1 header band heights (type4 locks to food-menu set1 chrome).
   * Spacer must match real chrome — see `menuFlowHeaderLayout`.
   */
  const { fullHeaderHeight, collapsedHeaderHeight } = useMemo(() => resolveMenuFlowStudioHeaderHeights(), []);

  const menu_list_header_height = fullHeaderHeight;

  // REFINEMENT: Optimized scroll handler with direct state sync
  const onScroll = useAnimatedScrollHandler({
    onScroll: e => {
      'worklet';
      const y = e.contentOffset.y;
      scrollY.value = y;

      const isCollapsed = y > menu_scroll_range / 2;
      if (lastDateSliderCollapsed.value !== isCollapsed) {
        lastDateSliderCollapsed.value = isCollapsed;
      }

      // Sync section index on UI thread directly
      const collapsed = collapsedHeaderBottomSV.value;
      const offsets = sectionOffsetsSV.value;
      if (y <= menu_scroll_range) {
        currentSectionIndexSV.value = 0;
        return;
      }
      const threshold = collapsed + y;
      let idx = 0;
      for (let i = 0; i < offsets.length; i++) {
        if (offsets[i] <= threshold) {
          idx = i;
        } else {
          break;
        }
      }
      if (currentSectionIndexSV.value !== idx) {
        currentSectionIndexSV.value = idx;
      }
    },
  });

  const showToastMessage = useCallback((msg: string, variant: ToastVariant = 'default') => {
    setToastMessage(msg);
    setToastVariant(variant);
    setShowToast(true);
  }, []);

  const openPreviewSelectionBlockedPrompt = useCallback(() => {
    if (is_guest_from_main) {
      promptAccountRequiredAlert(t, { openLogin, openRegister });
      return;
    }
    Alert.alert(t('menu.subscriptionRequiredTitle'), t('menu.subscriptionRequiredForSelection'), [
      { text: t('common.cancel'), style: 'cancel' },
      {
        text: t('menu.guestBrowseViewPlans'),
        onPress: () => {
          (navigation as { navigate: (name: string, params?: object) => void }).navigate('SubscriptionList');
        },
      },
    ]);
  }, [is_guest_from_main, t, openLogin, openRegister, navigation]);

  const showToastMessageRef = useRef(showToastMessage);
  showToastMessageRef.current = showToastMessage;

  useEffect(() => {
    setOnSyncError((date: string, message: string) => {
      showToastMessageRef.current(`${moment(date).format('MMM D')}: ${message}`);
    });
  }, [setOnSyncError]);

  useEffect(() => {
    if (is_date_dirty) {
      setV3SaveUi(s => {
        if (s === 'saving') {
          return s;
        }
        if (s === 'error') {
          return 'error';
        }
        // New local edits after a success ('saved') or baseline ('synced') → show unsaved/dirty, not a stale "Saved" chip.
        return 'dirty';
      });
    } else {
      setV3SaveUi(s => {
        if (s === 'saving' || s === 'saved') {
          return s;
        }
        if (s === 'error') {
          return 'error';
        }
        return 'synced';
      });
    }
  }, [is_date_dirty]);

  useEffect(() => {
    if (v3_prev_date_ref.current != null && v3_prev_date_ref.current !== selectedDate) {
      setV3SaveUi('synced');
      setV3BarSuppressed(false);
      setV3PostSave(false);
    }
    v3_prev_date_ref.current = selectedDate;
  }, [selectedDate]);

  useEffect(() => {
    if (is_date_dirty) {
      setV3BarSuppressed(false);
    }
  }, [is_date_dirty]);

  useEffect(() => {
    return () => {
      if (v3_to_synced_ref.current) {
        clearTimeout(v3_to_synced_ref.current);
      }
      if (v3_hide_bar_ref.current) {
        clearTimeout(v3_hide_bar_ref.current);
      }
    };
  }, []);

  const foodTypeLabels = useMemo(() => {
    const labels: Record<string, string> = { All: t('menu.all') };
    if (!display_menu_builders) {
      return labels;
    }
    for (const b of display_menu_builders) {
      const val = b.type_name;
      if (val && !(val in labels)) {
        labels[val] = getApiText(b as unknown as Record<string, unknown>, 'type_name', 'a_type_name') || val;
      }
    }
    return labels;
  }, [display_menu_builders, t, locale]);

  const is_box2 = activeBox === 'box2';

  const tabbed_list_cache = useMemo(
    () =>
      buildMenuFlowTabbedListCache(display_menu_builders ?? [], {
        mealsCount,
        snacksCount,
        selectionLimit: storeDayConfig?.selectionLimit,
        fullHeaderHeight: menu_list_header_height,
        itemRowHeight: MENU_FLOW_ITEM_ROW_HEIGHT,
        sectionGap: SECTION_GAP,
      }),
    [display_menu_builders, mealsCount, snacksCount, storeDayConfig?.selectionLimit, menu_list_header_height],
  );

  const foodTypeTabs = tabbed_list_cache.tabs;

  const active_list_model = tabbed_list_cache.models[selectedFoodType] ?? tabbed_list_cache.models.All;
  const sections = active_list_model.sections;
  const flatRows = active_list_model.flatRows;
  const sectionOffsets = active_list_model.sectionOffsets;
  const rowOffsets = active_list_model.rowOffsets;

  useEffect(() => {
    if (!tabbed_list_cache.models[selectedFoodType]) {
      setSelectedFoodType('All');
    }
  }, [tabbed_list_cache, selectedFoodType]);
  const server_menu_snapshot = useMenuStore(
    s => s.serverSnapshotByDate[ensureAsciiDateString(s.selectedDate) || s.selectedDate],
  );
  const saved_baseline = useMemo(() => {
    const sn = server_menu_snapshot;
    if (!sn) {
      return { meals: 0, snacks: 0 };
    }
    if (Boolean(sn.isBox2Data) !== is_box2) {
      return { meals: 0, snacks: 0 };
    }
    return countMealsAndSnacksFromMenuBuilders(sn.menuBuilders);
  }, [server_menu_snapshot, is_box2]);
  const { data: menuData, isLoading: isLoadingMenu, isFetching: isFetchingMenu, isError: isMenuLoadError, error: menuLoadError, refetch: refetchMenu } =
    useMenuForDateQuery(menu_query_date, is_box2, {
      guest_menu_preview,
      guest_selectable_dates: guest_menu_preview ? preview_week_dates : undefined,
    });

  const dateSliderDates = useMemo(() => {
    if (preview_week_dates_mode) {
      return preview_week_dates;
    }
    if (!subscriptionData?.availableDates?.length) {
      return [];
    }
    const sorted = [...subscriptionData.availableDates].map(d => ensureAsciiDateString(d) || d).sort();
    const first = moment(sorted[0], 'YYYY-MM-DD');
    const last = moment(sorted[sorted.length - 1], 'YYYY-MM-DD');
    const dates: string[] = [];
    const current = first.clone();
    moment.locale('en');
    while (current.isSameOrBefore(last, 'day')) {
      dates.push(current.format('YYYY-MM-DD'));
      current.add(1, 'day');
    }
    return dates;
  }, [preview_week_dates_mode, preview_week_dates, subscriptionData?.availableDates]);

  /**
   * Resolve initial / default selected date for the menu tab.
   * Calendar navigation passes `{ date, fromCalendar: true }` — that must override any existing
   * `selectedDate` (e.g. from prefetch or a previous session); the old guard `|| selectedDate` prevented that.
   */
  useEffect(() => {
    if (preview_week_dates_mode) {
      return;
    }
    if (!subscriptionData?.availableDates?.length) {
      return;
    }
    const available_ascii = subscriptionData.availableDates.map(d => ensureAsciiDateString(d) || d);
    const normalized_calendar = initialDateFromCalendar ? ensureAsciiDateString(initialDateFromCalendar) || initialDateFromCalendar : null;

    if (from_calendar_nav && normalized_calendar) {
      if (available_ascii.includes(normalized_calendar) && !forcedDisabledDateSet.has(normalized_calendar)) {
        if (selectedDate !== normalized_calendar) {
          setSelectedDate(normalized_calendar);
          const config = getDayConfig(normalized_calendar) ?? subscriptionData?.dayConfigMap?.[normalized_calendar];
          const is_double = config?.isDoubleBox ?? false;
          if (config) {
            setMenuData([], normalized_calendar, { ...config, isDoubleBox: is_double }, false);
          }
        }
      }
      navigation.setParams({ date: undefined, fromCalendar: undefined } as never);
      return;
    }

    if (selectedDate) {
      return;
    }

    let dateToUse = normalized_calendar && available_ascii.includes(normalized_calendar) && !forcedDisabledDateSet.has(normalized_calendar) ? normalized_calendar : undefined;
    if (!dateToUse) {
      moment.locale('en');
      const today = moment().format('YYYY-MM-DD');
      const first_selectable = available_ascii.find(d => !forcedDisabledDateSet.has(d));
      if (available_ascii.includes(today) && !forcedDisabledDateSet.has(today)) {
        dateToUse = today;
      } else {
        dateToUse = first_selectable ?? available_ascii[0];
      }
    }
    if (dateToUse && available_ascii.includes(dateToUse)) {
      setSelectedDate(dateToUse);
      const config = getDayConfig(dateToUse) ?? subscriptionData?.dayConfigMap?.[dateToUse];
      const is_double = config?.isDoubleBox ?? false;
      if (config) {
        setMenuData([], dateToUse, { ...config, isDoubleBox: is_double }, false);
      }
    }
  }, [preview_week_dates_mode, subscriptionData, initialDateFromCalendar, from_calendar_nav, selectedDate, getDayConfig, setSelectedDate, setMenuData, navigation, forcedDisabledDateSet]);

  useEffect(() => {
    if (preview_week_dates_mode || !subscriptionData?.availableDates?.length) {
      return;
    }
    const available_ascii = subscriptionData.availableDates.map(d => ensureAsciiDateString(d) || d);
    if (!selectedDate || (available_ascii.includes(selectedDate) && !forcedDisabledDateSet.has(selectedDate))) {
      return;
    }
    moment.locale('en');
    const today = moment().format('YYYY-MM-DD');
    const first_selectable = available_ascii.find(d => !forcedDisabledDateSet.has(d));
    const dateToUse = available_ascii.includes(today) && !forcedDisabledDateSet.has(today) ? today : (first_selectable ?? available_ascii[0]!);
    setSelectedDate(dateToUse);
    const config = getDayConfig(dateToUse) ?? subscriptionData?.dayConfigMap?.[dateToUse];
    const is_double = config?.isDoubleBox ?? false;
    if (config) {
      setMenuData([], dateToUse, { ...config, isDoubleBox: is_double }, false);
    }
  }, [preview_week_dates_mode, subscriptionData, selectedDate, getDayConfig, setSelectedDate, setMenuData, forcedDisabledDateSet]);

  useEffect(() => {
    if (!preview_week_dates_mode || preview_week_dates.length === 0) {
      return;
    }
    const normalized_calendar = initialDateFromCalendar ? ensureAsciiDateString(initialDateFromCalendar) || initialDateFromCalendar : null;

    if (from_calendar_nav && normalized_calendar) {
      const selectable = resolveGuestDatesIncluding(normalized_calendar);
      ensureGuestDateInRange(normalized_calendar);
      const target = selectable.includes(normalized_calendar) ? normalized_calendar : guestDisplayMenuTodayIso();
      if (selectedDate !== target) {
        setSelectedDate(target);
        setMenuData([], target, null, false);
      }
      navigation.setParams({ date: undefined, fromCalendar: undefined } as never);
      return;
    }

    if (selectedDate && preview_week_dates.includes(selectedDate)) {
      return;
    }

    const today = guestDisplayMenuTodayIso();
    setSelectedDate(today);
    setMenuData([], today, null, false);
  }, [
    preview_week_dates_mode,
    preview_week_dates,
    initialDateFromCalendar,
    from_calendar_nav,
    selectedDate,
    setSelectedDate,
    setMenuData,
    navigation,
    ensureGuestDateInRange,
    resolveGuestDatesIncluding,
  ]);

  const isDoubleBoxForDate =
    !preview_week_dates_mode &&
    Boolean(double_box_anchor_set.has(selectedDate) || getDayConfig(selectedDate)?.isDoubleBox);

  /** Gate for enabling a *new* double-box only (API anchors skip this check). */
  const can_enable_new_double_box = useMemo(() => {
    if (preview_week_dates_mode || !selectedDate) {
      return false;
    }
    const sub_id = getDayConfig(selectedDate)?.selectedSubscriptionID;
    const sub =
      typeof sub_id === 'number'
        ? subscriptionData.subscriptions.find(s => s.id === sub_id)
        : subscriptionData.subscriptions[0];
    return canEnableNewDoubleBoxForDate(
      selectedDate,
      sub,
      subscriptionData.subscriptions,
      subscriptionData.availableDates ?? [],
      subscription_context.config,
    );
  }, [
    preview_week_dates_mode,
    selectedDate,
    getDayConfig,
    subscriptionData.subscriptions,
    subscriptionData.availableDates,
    subscription_context.config,
  ]);

  useEffect(() => {
    if (!selectedDate || !isDoubleBoxForDate) {
      setActiveBox('box1');
    }
  }, [selectedDate, isDoubleBoxForDate]);

  const last_set_menu_key_ref = useRef<string>('');

  useEffect(() => {
    const merge_date_ascii = ensureAsciiDateString(selectedDate) || selectedDate;
    const merge_date =
      merge_date_ascii.length >= 10 ? merge_date_ascii : menu_query_date.length >= 10 ? menu_query_date : '';
    if (!merge_date) {
      return;
    }
    const slot = getSlot(merge_date);
    const query_builders = menuData?.menuBuilders ?? [];
    const { builders: merged, builders_from_slot, slot_matches_box } = pickBuildersForMenuMerge(
      slot,
      query_builders,
      is_box2,
    );
    const query_item_count = query_builders.reduce((n, b) => n + (b.type_data?.length ?? 0), 0);
    const slot_item_count = (builders_from_slot ?? []).reduce((n, b) => n + (b.type_data?.length ?? 0), 0);
    // Prefer fuller GET when the slot was previously allergy-shrunk and stuck.
    const builders = query_item_count > slot_item_count ? query_builders : merged;
    const raw_config = getDayConfig(merge_date) ?? subscriptionData?.dayConfigMap?.[merge_date] ?? null;
    const is_double = is_box2 || (raw_config?.isDoubleBox ?? false);
    const config = raw_config ? { ...raw_config, isDoubleBox: is_double } : null;
    const key = `${merge_date}|${is_box2}|${selected_allergy_ids.join(',')}|${selected_dislike_ids.join(',')}`;
    // Include query fingerprint so a later non-empty GET still applies, but identical
    // re-runs (unstable getDayConfig / allergy array refs) cannot loop setMenuData.
    const apply_key = `${key}|q:${query_builders.length}:${query_item_count}`;
    if (last_set_menu_key_ref.current === apply_key) {
      return;
    }
    if (builders.length === 0 && slot_matches_box && (slot?.menuBuilders?.length ?? 0) === 0) {
      return;
    }
    last_set_menu_key_ref.current = apply_key;
    const enriched =
      builders.length > 0
        ? prepareMenuBuildersWithHealthPolicy(builders, selected_allergy_ids, selected_dislike_ids)
        : [];
    setMenuData(enriched, merge_date, config, is_box2);
  }, [menuData, selectedDate, menu_query_date, is_box2, subscriptionData?.dayConfigMap, setMenuData, getSlot, selected_allergy_ids, selected_dislike_ids]);

  useFocusEffect(
    useCallback(() => {
      return () => {
        const { customerId: cid, selectedDate: date } = leaveRef.current;
        if (typeof cid === 'number' && date) {
          const date_ascii = ensureAsciiDateString(date) || date;
          prefetchMenuDateInBackground(date_ascii);
        }
      };
    }, [prefetchMenuDateInBackground]),
  );

  const is_menu_save_allowed = useCallback(() => {
    const m = selectedMealsCount;
    const s = selectedSnacksCount;
    if (m === 0 && s === 0) {
      return true;
    }
    return m === mealsCount && s === snacksCount;
  }, [selectedMealsCount, selectedSnacksCount, mealsCount, snacksCount]);

  const build_incomplete_remaining_label = useCallback(() => {
    const meal_rem = Math.max(0, mealsCount - selectedMealsCount);
    const snack_rem = Math.max(0, snacksCount - selectedSnacksCount);
    const parts: string[] = [];
    if (meal_rem > 0) {
      parts.push(`${meal_rem} ${meal_rem === 1 ? t('menu.meal') : t('menu.meals')}`);
    }
    if (snack_rem > 0) {
      parts.push(`${snack_rem} ${snack_rem === 1 ? t('menu.snack') : t('menu.snacks')}`);
    }
    return parts.join(` ${t('common.and')} `);
  }, [mealsCount, selectedMealsCount, snacksCount, selectedSnacksCount, t]);

  const applyDateChange = useCallback(
    (newDate: string) => {
      if (newDate === selectedDate) {
        return;
      }
      const date_left_behind = ensureAsciiDateString(selectedDate) || selectedDate;
      setSelectedDate(newDate);
      setActiveBox('box1');
      const existing_slot = getSlot(newDate);
      if (!(existing_slot && existing_slot.menuBuilders.length > 0)) {
        const config = getDayConfig(newDate) ?? subscriptionData?.dayConfigMap?.[newDate] ?? null;
        const is_double = config?.isDoubleBox ?? false;
        setMenuData([], newDate, config ? { ...config, isDoubleBox: is_double } : null, false);
      }
      if (date_left_behind) {
        prefetchMenuDateInBackground(date_left_behind);
      }
    },
    [selectedDate, getSlot, getDayConfig, subscriptionData?.dayConfigMap, setSelectedDate, setMenuData, prefetchMenuDateInBackground],
  );

  const handleMenuSave = useCallback(async () => {
    if (!is_menu_save_allowed()) {
      Alert.alert(t('menu.saveIncompleteTitle'), t('menu.saveIncompleteMessage'));
      return;
    }
    const sel_key = ensureAsciiDateString(selectedDate) || selectedDate;
    if (!useMenuStore.getState().isDateDivergentFromServer(sel_key) || isRequestSyncing) {
      return;
    }
    if (v3_to_synced_ref.current) {
      clearTimeout(v3_to_synced_ref.current);
      v3_to_synced_ref.current = null;
    }
    if (v3_hide_bar_ref.current) {
      clearTimeout(v3_hide_bar_ref.current);
      v3_hide_bar_ref.current = null;
    }
    setV3BarSuppressed(false);
    setV3PostSave(true);
    setV3SaveUi('saving');
    const date_key = ensureAsciiDateString(selectedDate) || selectedDate;
    await flushSyncForDate(date_key);
    if (useMenuStore.getState().isDateDivergentFromServer(date_key)) {
      setV3PostSave(false);
      setV3SaveUi('error');
      return;
    }
    setV3SaveUi('saved');
    showToastMessage(t('menu.saveSuccessful'), 'success');
    v3_to_synced_ref.current = setTimeout(() => {
      v3_to_synced_ref.current = null;
      setV3SaveUi('synced');
    }, SAVED_TO_SYNCED_CHIP_MS);
    v3_hide_bar_ref.current = setTimeout(() => {
      v3_hide_bar_ref.current = null;
      setV3PostSave(false);
      setV3BarSuppressed(true);
    }, POST_SAVE_BAR_MS);
    if (typeof customerId === 'number' && isMenuInvalidationAllowedForDate(date_key)) {
      queryClient.invalidateQueries({ queryKey: ['menu', customerId, date_key] });
    }
    if (isSelectionComplete() && (selectedMealsCount > 0 || selectedSnacksCount > 0)) {
      const sorted = [...(subscriptionData?.availableDates ?? [])].map(d => ensureAsciiDateString(d) || d).sort();
      const idx = sorted.indexOf(date_key);
      let next_date: string | null = null;
      for (let j = idx + 1; j < sorted.length; j += 1) {
        const candidate = sorted[j] ?? '';
        if (candidate && !forcedDisabledDates.includes(candidate)) {
          next_date = candidate;
          break;
        }
      }
      if (next_date) {
        const formatted = moment(next_date).format('Do MMMM, YYYY');
        const complete_body = t('menu.menuCompleteMessage').replace('{{date}}', formatted);
        Alert.alert(t('menu.menuCompleteTitle'), complete_body, [
          { text: t('common.cancel'), style: 'cancel' },
          {
            text: t('common.yes'),
            onPress: () => applyDateChange(next_date!),
          },
        ]);
      }
    }
  }, [
    is_menu_save_allowed,
    selectedDate,
    isRequestSyncing,
    flushSyncForDate,
    showToastMessage,
    t,
    customerId,
    queryClient,
    isSelectionComplete,
    selectedMealsCount,
    selectedSnacksCount,
    subscriptionData?.availableDates,
    forcedDisabledDates,
    applyDateChange,
  ]);

  const handleDateChange = useCallback(
    (newDate: string) => {
      if (newDate === selectedDate) {
        return;
      }
      if (!preview_week_dates_mode && forcedDisabledDateSet.has(newDate)) {
        return;
      }
      if (preview_week_dates_mode) {
        ensureGuestDateInRange(newDate);
        applyDateChange(newDate);
        return;
      }

      if (!save_menu_flow) {
        const date_left_behind = selectedDate;
        flushSyncForDate(date_left_behind).then(() => applyDateChange(newDate));
        return;
      }

      const n_new = ensureAsciiDateString(newDate) || newDate;
      const n_cur = ensureAsciiDateString(selectedDate) || selectedDate;
      if (n_new === n_cur) {
        return;
      }
      if (!useMenuStore.getState().isDateDivergentFromServer(n_cur)) {
        applyDateChange(newDate);
        return;
      }
      const has_no = selectedMealsCount === 0 && selectedSnacksCount === 0;
      const has_full = selectedMealsCount === mealsCount && selectedSnacksCount === snacksCount;
      const is_partial = !has_no && !has_full;
      const go = (rollback: boolean) => {
        if (rollback) {
          useMenuStore.getState().rollbackDate(selectedDate);
        }
        applyDateChange(newDate);
      };
      if (is_partial) {
        const remaining = build_incomplete_remaining_label();
        const incomplete_body = t('menu.incompleteSelectionsMessage').replace('{{remaining}}', remaining);
        Alert.alert(t('menu.incompleteSelectionsTitle'), incomplete_body, [
          { text: t('common.cancel'), style: 'cancel' },
          { text: t('common.ok'), onPress: () => go(true) },
        ]);
        return;
      }
      Alert.alert(t('menu.unsavedChangesTitle'), t('menu.unsavedChangesMessage'), [
        { text: t('common.cancel'), style: 'cancel' },
        { text: t('common.ok'), onPress: () => go(true) },
      ]);
    },
    [
      selectedDate,
      preview_week_dates_mode,
      forcedDisabledDateSet,
      applyDateChange,
      ensureGuestDateInRange,
      save_menu_flow,
      flushSyncForDate,
      selectedMealsCount,
      selectedSnacksCount,
      mealsCount,
      snacksCount,
      t,
      build_incomplete_remaining_label,
    ],
  );

  const applyQuantityChange = useCallback(
    (menu_builder_id: number, itemId: number, isPlusClick: boolean) => {
      const result = updateItemQuantity(menu_builder_id, itemId, isPlusClick);
      if (!result.success) {
        showToastMessage(result.reason ?? t('menu.cannotChangeQuantity'));
      }
    },
    [updateItemQuantity, t, showToastMessage],
  );

  const [replaceableModalItem, setReplaceableModalItem] = useState<MenuItemData | null>(null);
  const handleReplaceableSelect = useCallback(
    (replaceableItemId: number, menu_builder_id: number) => {
      setReplaceableModalItem(null);
      applyQuantityChange(menu_builder_id, replaceableItemId, true);
    },
    [applyQuantityChange],
  );

  const openCustomizationModalForItem = useCallback((item: MenuItemData, itemId: number) => {
    pendingPlusItemIdRef.current = itemId;
    pendingPlusMenuBuilderIdRef.current = item.menu_builder_id ?? item.food_type_id;
    setCustomizationItem(item);
    setCustomizationModalVisible(true);
  }, []);

  const handleItemQuantityChange = useCallback(
    (menu_builder_id: number, itemId: number, isPlusClick: boolean) => {
      if (preview_week_dates_mode) {
        openPreviewSelectionBlockedPrompt();
        return;
      }
      const state = useMenuStore.getState();
      const current_builders = state.getSlot(state.selectedDate)?.menuBuilders ?? [];
      const item = findMenuItemByBuilderAndId(current_builders, menu_builder_id, itemId);
      if (isPlusClick && item) {
        const health_code = typeof (item as any).health_code === 'number' ? (item as any).health_code : 0;
        const has_customizations = (item.is_customizations_available ?? 0) === 1 && Array.isArray(item.customizations) && item.customizations.length > 0;

        if (health_code !== 0) {
          Alert.alert(
            t('menu.healthWarningTitle'),
            t('menu.healthWarningMessage'),
            [
              { text: t('common.no'), style: 'cancel' },
              {
                text: t('common.yes'),
                onPress: () => {
                  if (has_customizations) {
                    setTimeout(() => openCustomizationModalForItem(item, itemId), 100);
                  } else {
                    applyQuantityChange(menu_builder_id, itemId, isPlusClick);
                  }
                },
              },
            ],
          );
          return;
        }
        if (has_customizations) {
          openCustomizationModalForItem(item, itemId);
          return;
        }
      }
      applyQuantityChange(menu_builder_id, itemId, isPlusClick);
    },
    [t, applyQuantityChange, openCustomizationModalForItem, preview_week_dates_mode, openPreviewSelectionBlockedPrompt],
  );

  const handleCustomizationApply = useCallback(
    (customizations: CustomizationGroup[]) => {
      const itemId = pendingPlusItemIdRef.current;
      const menu_builder_id = pendingPlusMenuBuilderIdRef.current;
      setCustomizationModalVisible(false);
      setCustomizationItem(null);
      pendingPlusItemIdRef.current = null;
      pendingPlusMenuBuilderIdRef.current = null;
      if (typeof itemId === 'number' && typeof menu_builder_id === 'number') {
        setItemCustomizations(menu_builder_id, itemId, customizations);
        const result = updateItemQuantity(menu_builder_id, itemId, true);
        if (!result.success) {
          showToastMessage(result.reason ?? t('menu.cannotChangeQuantity'));
        }
      }
    },
    [setItemCustomizations, updateItemQuantity, t, showToastMessage],
  );

  const handleCustomizationCancel = useCallback(() => {
    setCustomizationModalVisible(false);
    setCustomizationItem(null);
    pendingPlusItemIdRef.current = null;
    pendingPlusMenuBuilderIdRef.current = null;
  }, []);

  const handleScaledCarbsChange = useCallback(
    (menu_builder_id: number, itemId: number, value: number) => {
      if (preview_week_dates_mode) {
        return;
      }
      updateItemScaledCarbs(menu_builder_id, itemId, value);
    },
    [updateItemScaledCarbs, preview_week_dates_mode],
  );

  const applyBoxSwitch = useCallback(
    (box: 'box1' | 'box2') => {
      void (async () => {
        await flushSyncForDate(selectedDate);
        setActiveBox(box);
        if (box === 'box2') {
          setPendingDoubleBoxDates(prev => new Set(prev).add(selectedDate));
        }
      })();
    },
    [selectedDate, flushSyncForDate],
  );

  const handleBoxSelect = useCallback(
    (box: 'box1' | 'box2') => {
      if (preview_week_dates_mode) {
        return;
      }
      if (!save_menu_flow) {
        void applyBoxSwitch(box);
        return;
      }
      const d_cur = ensureAsciiDateString(selectedDate) || selectedDate;
      if (useMenuStore.getState().isDateDivergentFromServer(d_cur)) {
        Alert.alert(t('menu.unsavedChangesTitle'), t('menu.unsavedChangesMessage'), [
          { text: t('common.cancel'), style: 'cancel' },
          {
            text: t('common.ok'),
            onPress: () => {
              useMenuStore.getState().rollbackDate(selectedDate);
              applyBoxSwitch(box);
            },
          },
        ]);
        return;
      }
      applyBoxSwitch(box);
    },
    [selectedDate, t, applyBoxSwitch, preview_week_dates_mode, save_menu_flow],
  );

  const handleRequestDoubleBox = useCallback(() => {
    if (preview_week_dates_mode) {
      return;
    }
    if (!can_enable_new_double_box) {
      showToastMessage(t('menu.doubleBoxUnavailable'));
      return;
    }
    setPendingDoubleBoxDates(prev => new Set(prev).add(selectedDate));
    if (typeof customerId === 'number' && selectedDate) {
      patchSubscriptionDoubleBoxDateInCache(queryClient, customerId, selectedDate, true);
      scheduleDeferredSubscriptionRefetch(refetchSubscription);
    }
    const already_double = getDayConfig(selectedDate)?.isDoubleBox === true;
    if (!already_double) {
      const raw_config = getDayConfig(selectedDate) ?? subscriptionData?.dayConfigMap?.[selectedDate] ?? null;
      const config = raw_config ? { ...raw_config, isDoubleBox: true } : null;
      setMenuData([], selectedDate, config, true);
    }
    setActiveBox('box2');
  }, [
    selectedDate,
    getDayConfig,
    subscriptionData?.dayConfigMap,
    setMenuData,
    preview_week_dates_mode,
    customerId,
    queryClient,
    refetchSubscription,
    can_enable_new_double_box,
    showToastMessage,
    t,
  ]);

  const { mutateAsync: bookMenu } = useMenuBooking();
  const handleCancelBox = useCallback(async () => {
    if (typeof customerId !== 'number' || !selectedDate || selectedDate.length < 10) {
      return;
    }
    const subscription_id = getDayConfig(selectedDate)?.selectedSubscriptionID;
    if (typeof subscription_id !== 'number') {
      showToastMessage(t('menu.cancelBoxError'));
      return;
    }
    try {
      await bookMenu({
        customer_id: customerId,
        customer_subscription_id: subscription_id,
        date: selectedDate,
        remove_box_index: 1,
        menu_items: [],
        is_double_box: false,
      });
      if (isMenuInvalidationAllowedForDate(ensureAsciiDateString(selectedDate) || selectedDate)) {
        queryClient.invalidateQueries({ queryKey: ['menu', customerId, ensureAsciiDateString(selectedDate) || selectedDate] });
      }
      await refetchSubscription();
      setPendingDoubleBoxDates(prev => {
        const next = new Set(prev);
        next.delete(selectedDate);
        return next;
      });
      patchSubscriptionDoubleBoxDateInCache(queryClient, customerId, selectedDate, false);
      setActiveBox('box1');
      refetchMenu();
      showToastMessage(t('menu.cancelBoxSuccess'));
    } catch (err: any) {
      showToastMessage(err?.message ?? t('menu.cancelBoxError'));
    }
  }, [customerId, selectedDate, queryClient, getDayConfig, bookMenu, refetchSubscription, refetchMenu, showToastMessage, t]);

  useEffect(() => {
    sectionOffsetsSV.value = sectionOffsets;
  }, [sectionOffsets, sectionOffsetsSV]);

  const collapsed_header_bottom = useMemo(
    () => collapsedHeaderHeight + insets.top,
    [collapsedHeaderHeight, insets.top],
  );

  useLayoutEffect(() => {
    collapsedHeaderBottomSV.value = collapsed_header_bottom;
  }, [collapsed_header_bottom, collapsedHeaderBottomSV]);

  const getItemLayoutFlat = useCallback(
    (_data: ArrayLike<MenuFlowFlatRow> | null | undefined, index: number) => {
      const row = flatRows[index];
      const length = row?.type === 'item' ? MENU_FLOW_ITEM_ROW_HEIGHT : SECTION_GAP;
      const offset = rowOffsets[index] ?? menu_list_header_height;
      return { length, offset, index };
    },
    [flatRows, rowOffsets, menu_list_header_height],
  );

  const keyExtractorFlat = useCallback((row: MenuFlowFlatRow) => (row.type === 'item' ? row.item.rowKey : row.rowKey), []);

  const show_macro_bar = useMemo(() => {
    if (!storeDayConfig) {
      return false;
    }
    if (preview_week_dates_mode) {
      return selectedMealsCount + selectedSnacksCount > 0;
    }
    if (!save_menu_flow) {
      return selectedMealsCount > 0 || selectedSnacksCount > 0 || is_menu_syncing;
    }
    if (is_date_dirty) {
      return true;
    }
    if (isRequestSyncing) {
      return true;
    }
    if (v3SaveUi === 'saving' || v3SaveUi === 'saved' || v3SaveUi === 'error') {
      return true;
    }
    if (v3PostSave) {
      return true;
    }
    if (v3BarSuppressed) {
      return false;
    }
    const current_total = selectedMealsCount + selectedSnacksCount;
    const saved_total = saved_baseline.meals + saved_baseline.snacks;
    if (current_total > 0) {
      return true;
    }
    if (current_total === 0 && saved_total === 0) {
      return false;
    }
    if (current_total === 0 && saved_total > 0) {
      return true;
    }
    return false;
  }, [
    storeDayConfig,
    preview_week_dates_mode,
    save_menu_flow,
    is_menu_syncing,
    is_date_dirty,
    isRequestSyncing,
    v3SaveUi,
    v3PostSave,
    v3BarSuppressed,
    selectedMealsCount,
    selectedSnacksCount,
    saved_baseline,
  ]);

  const show_macro_bar_resolved = useMemo(() => {
    if (!show_macro_bar) {
      return false;
    }
    if (nutrition_ui_enabled) {
      return true;
    }
    if (save_menu_flow) {
      return true;
    }
    return is_menu_syncing;
  }, [show_macro_bar, nutrition_ui_enabled, save_menu_flow, is_menu_syncing]);

  const guest_footer_scroll_pad = show_guest_back_footer ? guestBrowseBackFooterScrollPad(insets.bottom) : 0;
  const list_content_bottom_pad =
    (show_macro_bar_resolved ? getMenuListBottomContentPaddingResolved(insets.bottom) : BOTTOM_BAR_PADDING) + guest_footer_scroll_pad;

  const listContentStyle = useMemo(
    () => [styles.sectionsContent, { paddingBottom: list_content_bottom_pad }],
    [list_content_bottom_pad, styles.sectionsContent],
  );

  const display_meals_count = preview_week_dates_mode && mealsCount === 0 ? 3 : mealsCount;
  const display_snacks_count = preview_week_dates_mode && snacksCount === 0 ? 2 : snacksCount;
  const probe_selection_disabled = shouldDisableMenuSelectionForProbe(selectedDate, preview_week_dates_mode);

  const renderFlatRow = useCallback(
    ({ item: row }: { item: MenuFlowFlatRow }) =>
      row.type === 'item' ? (
        <View style={flat_item_wrap_style}>
          <MenuFlowItemCardResolved
            themeStyleKey={menuItemThemeKey}
            item={row.item.item}
            menuBuilderId={row.item.section.foodTypeId}
            onQuantityChange={handleItemQuantityChange}
            mealsCount={display_meals_count}
            snacksCount={display_snacks_count}
            isForceFixedMenu={storeDayConfig?.isForceFixedMenu ?? false}
            selectionDisabled={preview_week_dates_mode || probe_selection_disabled}
            onSelectionDisabledInteraction={preview_week_dates_mode ? openPreviewSelectionBlockedPrompt : undefined}
            isGroupLimitReached={row.item.section.selectedQuantity >= row.item.section.displayLimit}
            onLimitExceeded={showToastMessage}
            isVariableCarbs={!preview_week_dates_mode && (storeDayConfig?.is_variable_carbs ?? false)}
            defaultScaledCarbs={storeDayConfig?.default_scaled_carbs ?? 50}
            maxScaledCarbs={storeDayConfig?.max_scaled_carbs ?? 100}
            onScaledCarbsChange={handleScaledCarbsChange}
            reduceMotion={reduceMotion}
          />
        </View>
      ) : (
        <View style={styles.sectionDivider} />
      ),
    [
      styles,
      menuItemThemeKey,
      display_meals_count,
      display_snacks_count,
      storeDayConfig?.isForceFixedMenu,
      storeDayConfig?.is_variable_carbs,
      storeDayConfig?.default_scaled_carbs,
      storeDayConfig?.max_scaled_carbs,
      handleItemQuantityChange,
      showToastMessage,
      handleScaledCarbsChange,
      preview_week_dates_mode,
      probe_selection_disabled,
      openPreviewSelectionBlockedPrompt,
      reduceMotion,
      MenuFlowItemCardResolved,
    ],
  );

  const listEmptyComponent = useMemo(
    () =>
      isLoadingMenu || (isFetchingMenu && menuBuilders.length === 0) ? (
        <View style={styles.loadingContainer}>
          <MainLoader size={48} color={themeColors?.primary ?? appTheme.colors.primary} />
          <Text style={styles.loadingText}>{t('menu.loadingMenu')}</Text>
        </View>
      ) : isMenuLoadError ? (
        <IssueReportState
          compact
          issue_context={{
            page: t('tabs.menu'),
            issue: 'Failed to load menu for selected date',
            kind: 'api',
            api_endpoint: 'menu_builders',
            http_method: 'GET',
          }}
          error={menuLoadError}
          onRetry={() => refetchMenu()}
        />
      ) : (
        <View style={styles.emptyContainer}>
          <Text style={styles.emptyText}>{t('menu.noItemsForDate')}</Text>
        </View>
      ),
    [
      isLoadingMenu,
      isFetchingMenu,
      isMenuLoadError,
      menuLoadError,
      menuBuilders.length,
      refetchMenu,
      t,
      styles.loadingContainer,
      styles.loadingText,
      styles.emptyContainer,
      styles.emptyText,
      themeColors?.primary,
    ],
  );

  const listHeaderSpacer = useMemo(
    () => <View style={{ height: menu_list_header_height }} />,
    [menu_list_header_height],
  );

  const shell_colors = themeColors ?? appTheme.colors;

  if (should_wait_for_subscriptions) {
    return (
      <ScreenShell colors={shell_colors}>
        <View style={styles.container}>
          <View style={[styles.contentLayer, styles.loadingContainer]}>
            <MainLoader size={48} color={shell_colors.primary} />
          </View>
        </View>
      </ScreenShell>
    );
  }

  const menu_flow_header = (
    <MenuFlowHeaderResolved
      scrollY={scrollY}
      scrollRange={menu_scroll_range}
      fullHeaderHeight={fullHeaderHeight}
      collapsedHeaderHeight={collapsedHeaderHeight}
      dateSliderDates={dateSliderDates}
      selectableDates={preview_week_dates_mode ? preview_week_dates : (subscriptionData?.availableDates ?? [])}
      selectedDate={selectedDate}
      onDateSelect={handleDateChange}
      dayConfigMap={subscriptionData.dayConfigMap}
      foodTypes={foodTypeTabs}
      foodTypeLabels={foodTypeLabels}
      selectedFoodType={selectedFoodType}
      onSelectFoodType={handleSelectFoodType}
      mealsCount={display_meals_count}
      snacksCount={display_snacks_count}
      selectedMealsCount={selectedMealsCount}
      selectedSnacksCount={selectedSnacksCount}
      isSyncing={save_menu_flow ? isRequestSyncing : is_menu_syncing}
      lastDateSliderCollapsedSV={lastDateSliderCollapsed}
      reduceMotion={reduceMotion}
      sections={sections}
      currentSectionIndexSV={currentSectionIndexSV}
      isDoubleBoxForDate={isDoubleBoxForDate}
      activeBox={activeBox}
      onBoxSelect={preview_week_dates_mode ? undefined : handleBoxSelect}
      forcedDisabledDates={forcedDisabledDates}
      onCancelBox={preview_week_dates_mode ? undefined : handleCancelBox}
      onRequestDoubleBox={preview_week_dates_mode || !can_enable_new_double_box ? undefined : handleRequestDoubleBox}
    />
  );

  const menu_sections = (
    <View style={styles.sectionsWrap}>
      <Animated.View style={[styles.sectionsFlex, listOpacityStyle]}>
        <AnimatedFlatList
          ref={menuFlatListGestureRef}
          data={flatRows}
          extraData={activeBox}
          style={styles.sectionsFlex}
          onScroll={onScroll}
          scrollEventThrottle={16}
          contentContainerStyle={listContentStyle}
          ListHeaderComponent={listHeaderSpacer}
          keyExtractor={keyExtractorFlat}
          renderItem={renderFlatRow}
          getItemLayout={getItemLayoutFlat}
          ListEmptyComponent={listEmptyComponent}
          {...MENU_FLOW_FLAT_LIST_PROPS}
          contentInsetAdjustmentBehavior="never"
          automaticallyAdjustContentInsets={false}
          refreshControl={
            <RefreshControl
              refreshing={isPullRefreshing}
              onRefresh={() => {
                setIsPullRefreshing(true);
                refetchMenu().finally(() => setIsPullRefreshing(false));
              }}
            />
          }
        />
      </Animated.View>
    </View>
  );

  const menu_toast = (
    <View style={styles.toastLayer} pointerEvents="box-none" collapsable={false}>
      <Toast
        message={toastMessage}
        visible={showToast}
        variant={toastVariant}
        onHide={() => {
          setShowToast(false);
          setToastVariant('default');
        }}
      />
    </View>
  );

  const menu_overlays = (
    <>
      <MenuFlowBottomMacroBarResolved
        isVisible={show_macro_bar_resolved}
        macroTotals={macroTotals}
        isComplete={isSelectionComplete()}
        useSolidBackground={reduceMotion}
        isSyncing={save_menu_flow ? isRequestSyncing : is_menu_syncing}
        onPressSave={save_menu_flow && !preview_week_dates_mode && !probe_selection_disabled ? handleMenuSave : undefined}
        saveStatus={save_menu_flow ? v3SaveUi : 'synced'}
        saveButtonDisabled={
          !save_menu_flow ||
          v3SaveUi === 'saving' ||
          v3SaveUi === 'saved' ||
          (v3SaveUi === 'synced' && !is_date_dirty)
        }
        showMacroTotals={nutrition_ui_enabled}
      />

      <DailyCustomizationModal visible={customizationModalVisible} menuItem={customizationItem} onApply={handleCustomizationApply} onCancel={handleCustomizationCancel} />

      <Modal visible={!!replaceableModalItem} transparent animationType="fade">
        <Clickable style={styles.replaceableModalOverlay} onPress={() => setReplaceableModalItem(null)} accessibilityLabel={t('common.close')} accessibilityRole="button">
          <View style={styles.replaceableModalContent} onStartShouldSetResponder={() => true}>
            <Text style={styles.replaceableModalTitle}>{t('menu.chooseReplacement')}</Text>
            {(replaceableModalItem as any)?.replaceable_menu_items?.map((r: any) => (
              <Clickable
                key={r.id}
                onPress={() => handleReplaceableSelect(r.id, (r as any).menu_builder_id ?? replaceableModalItem?.menu_builder_id ?? replaceableModalItem?.food_type_id ?? 0)}
                style={styles.replaceableModalRow}
                accessibilityLabel={getApiText(r as Record<string, unknown>, 'name', 'a_name') || (r.name ?? r.title ?? String(r.id))}
                accessibilityRole="button">
                <View>
                  <Text style={styles.replaceableModalRowText}>{getApiText(r as Record<string, unknown>, 'name', 'a_name') || (r.name ?? r.title ?? `#${r.id}`)}</Text>
                </View>
              </Clickable>
            ))}
            <Clickable onPress={() => setReplaceableModalItem(null)} style={styles.replaceableModalCancel} accessibilityLabel={t('common.cancel')} accessibilityRole="button">
              <View>
                <Text style={styles.replaceableModalCancelText}>{t('common.cancel')}</Text>
              </View>
            </Clickable>
          </View>
        </Clickable>
      </Modal>

      {show_guest_back_footer ? <GuestBrowseBackFooter onBack={go_main} /> : null}
    </>
  );

  return (
    <ScreenShell colors={shell_colors}>
    <View style={styles.container}>
      <View style={styles.contentLayer}>
        {menu_flow_header}
        {menu_sections}
        {menu_overlays}
        {menu_toast}
      </View>
    </View>
    </ScreenShell>
  );
};

export default FoodDailyMenuFlowScreen;
